import { YStack, Form, Text } from "tamagui";
import { createPortal } from "react-dom";
import { OTPInputBlocks } from "@components/form/OTPInputBlocks";
import { containerStyle } from "@styles/index";
import { colors } from "@styles/colors";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosResponse } from "axios";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { useUser } from "@utils/store/user/useUser";
import { useForm, Controller, useWatch } from "react-hook-form"; // 這裡不需要 useFormContext 因為你在同一層
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import getURLSearchParam from "@utils/func/getURLSearchParam";
import Cookies from "js-cookie";
import { addHours } from "date-fns";
import liff from "@line/liff";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { RiCoupon2Line } from "react-icons/ri";
import { track } from "@lib/tracker";
import { isPracticeMode } from "@utils/practice/practiceMode";
import ScanViewport from "@screen/Onboarding/components/ScanViewport";

const InvitedCode = () => {
  // 練習模式：正常綁定前先模擬「相機掃描客戶序號 QR」；掃描動畫跑約 2 秒自動完成或略過後才進序號輸入。
  const [scanDone, setScanDone] = useState(false);
  const showScan = isPracticeMode() && !scanDone;
  // 模擬掃描：進入掃描畫面約 2 秒後自動辨識成功 → 進序號輸入（離開/略過會清掉 timer）。
  useEffect(() => {
    if (!showScan) return;
    const t = setTimeout(() => setScanDone(true), 2200);
    return () => clearTimeout(t);
  }, [showScan]);
  const { lineAccessToken } = useLineAuth();
  const { setLoading } = useUtilsStore();
  const { setInvGasData } = useUser();
  const navigate = useNavigate();
  const [thisInviteCode, setThisInviteCode] = useState<string | null>(null);

  //@ 1. URL / sessionStorage / Cookie 任一來源 → 帶入邀請碼
  useEffect(() => {
    const inviteCode = getURLSearchParam("invite") || sessionStorage.getItem("invite") || Cookies.get("invite");
    if (inviteCode) {
      Cookies.set("invite", inviteCode, {
        expires: addHours(new Date(), 24), //過期日(當下 + 24hr)
        path: "/",
        secure: true,
        sameSite: "Lax",
      });
      sessionStorage.setItem("invite", inviteCode);
      setThisInviteCode(inviteCode);
    }
  }, []);

  //@ 2. Resume：使用者跳離後回來時，URL/storage/cookie 可能被 LIFF / Safari ITP 清掉。
  //   問 BE 有沒有上次留下的 binding（spec app-line-login-phone-otp-spec.md §4.3）。
  //   背景靜默呼叫、不顯示 spinner；找到就同步寫回 storage/cookie + state。
  useEffect(() => {
    if (!lineAccessToken) return;
    const localInvite = getURLSearchParam("invite") || sessionStorage.getItem("invite") || Cookies.get("invite");
    if (localInvite) return; // 本地已有邀請碼，不需要問 BE

    handleRequest({
      asyncRequest: () =>
        api.post(
          "/app-api/line/authentication-code-check",
          { accessToken: lineAccessToken },
          { isAuth: false },
        ),
      successCallback: (res: AxiosResponse) => {
        const data = res.data?.data;
        if (data?.hasBinding && data?.authenticationCode) {
          Cookies.set("invite", data.authenticationCode, {
            expires: addHours(new Date(), 24),
            path: "/",
            secure: true,
            sameSite: "Lax",
          });
          sessionStorage.setItem("invite", data.authenticationCode);
          setThisInviteCode(data.authenticationCode);
        }
      },
      errorCallback: () => {}, // 靜默：使用者仍可手動輸入
    });
  }, [lineAccessToken]);

  //@ 建立 Form
  const formSchema = z.object({
    invCode: z.string().trim().min(1, { message: "請填寫客戶序號" }), //-- 必填設定
  });

  type FormData = z.infer<typeof formSchema>;
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      invCode: "", // 這裡設定可預設值
    },
  });
  const onSubmit = async (data: FormData) => {
    if (lineAccessToken) {
      track("funnel_step", { funnel: "register", step: "serial_submit" });
      handleRequest({
        asyncRequest: () =>
          api.post(
            "/app-api/line/authentication-validation",
            {
              accessToken: lineAccessToken,
              authenticationCode: data?.invCode || "",
            },
            { isAuth: false },
          ),
        successCallback: (res: AxiosResponse) => {
          setInvGasData(res.data.data);
          navigate("/register/verifyPhone");
        },
        errorCallback: (error: any) => {
          const { response } = error;
          if (error?.status === 404 && response?.data?.data?.contactUrl) {
            setLoading(true);
            window.location.href = response?.data?.data?.contactUrl || "";
          } else {
            setError("invCode", {
              type: "manual",
              message: "找不到此序號,請確認後重試",
            });
          }
        },
        finalCallback: () => {},
        setLoading,
      });
    } else {
      liff.login();
    }
  };

  //@ 3. thisInviteCode 一旦設定（來自 URL/storage/cookie 或 /check resume）→ 帶入表單欄位
  useEffect(() => {
    if (thisInviteCode) {
      setValue("invCode", thisInviteCode);
    }
  }, [thisInviteCode, setValue]);

  //@ 4. 表單欄位達 6 碼 → 自動 submit 觸發驗證
  const invCode = useWatch({ control, name: "invCode" });
  useEffect(() => {
    if (invCode?.trim().length === 6) {
      handleSubmit(onSubmit)();
    }
  }, [invCode]);

  // 練習模式：正常綁定前模擬「相機掃描客戶序號 QR」。整頁都是鏡頭、中間一個取景框、無 header；
  // 不開真實相機（ScanViewport 不給 onDecode 只顯示取景框＋掃描線動畫），約 2 秒自動辨識完成。
  // 用 fixed 全螢幕 portal 蓋掉 /register 外框 header，做成「整頁都是鏡頭」的感覺。
  if (showScan) {
    return createPortal(
      <div style={{ position: "fixed", inset: 0, zIndex: 100000, background: "#000" }}>
        <YStack width="100%" height="100%" position="relative">
          <ScanViewport fill>
            {/* 疊在鏡頭上：底部「掃描中…」+ 略過 */}
            <div className="absolute left-0 right-0 bottom-10 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 bg-black/55 rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-[#7fc0ff] animate-pulse" />
                <span className="text-[13px] text-white font-semibold">掃描中…</span>
              </div>
              <button
                type="button"
                onClick={() => setScanDone(true)}
                style={{ background: "none", border: "none", color: "rgba(255,255,255,0.8)", fontSize: 13, textDecoration: "underline", cursor: "pointer" }}
              >
                掃不到？直接輸入序號
              </button>
            </div>
          </ScanViewport>
        </YStack>
      </div>,
      document.body,
    );
  }

  return (
    <>
      <YStack {...containerStyle}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <YStack gap={15}>
            <RadiusCard>
              <RadiusBoxLabel label="客戶序號" icon={<RiCoupon2Line color={colors.primary} />} />
              <YStack mt="$3" alignItems="center">
                <Text fontSize={14} fontWeight="bold" color={colors.gray} mb="$3" alignSelf="flex-start">
                  請輸入瓦斯行提供的序號
                </Text>
                <Controller
                  control={control}
                  name="invCode"
                  render={({ field: { onChange, value } }) => (
                    <OTPInputBlocks value={value || ""} onChange={onChange} digits={6} error={errors.invCode?.message} />
                  )}
                />
                {errors.invCode && (
                  <Text color="$red10" fontSize={14} mt="$2" alignSelf="center">
                    {errors.invCode.message}
                  </Text>
                )}
              </YStack>
            </RadiusCard>
            <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
              <Text fontSize={14} color={colors.gray}>
                💡 序號可向您合作的瓦斯行索取
              </Text>
            </RadiusCard>
          </YStack>
        </Form>
      </YStack>
    </>
  );
};

export default InvitedCode;
